import React from "react";
import { useTranslation } from "react-i18next";
import Container from "Components/Container/Container";
import {
  Video,
  ShareFat,
  LockKey,
  Heart,
  ChatCircle,
  BookmarkSimple,
} from "@phosphor-icons/react";
import Spinner from "Components/RequestHandler/Spinner";

/**
 * Trailer action buttons.
 *
 * Desktop layout (lg+): UNCHANGED — absolute-positioned overlay at bottom-right
 * of the video with a bg-black/90 panel, primary on the right and secondary
 * icons on the left. All `lg:*` classes preserved verbatim.
 *
 * Mobile layout (<lg): restyled.
 *  - One layout (no isPlaying branch)
 *  - Primary button uses rounded-xl with a subtle primary shadow
 *  - Secondary icons are pill buttons with a dark-card look
 *    (bg-white/[0.06] border-white/[0.08]) matching HomeBanner / HeroActionCard
 *  - Icon sizes driven by Tailwind breakpoints, not JS state
 */

const SecondaryPill = ({
  onClick,
  active = false,
  disabled = false,
  label,
  children,
}) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    aria-label={label}
    title={label}
    className={`
      flex items-center gap-x-1.5 sm:gap-x-2
      p-2.5 sm:p-3 lg:p-3
      rounded-xl lg:rounded-md
      transition-colors
      bg-white/[0.06] border border-white/[0.08] hover:bg-white/[0.10]
      lg:bg-lightGrey lg:border-0 lg:hover:bg-lightGrey/90
      text-white disabled:opacity-60
      ${active ? "text-primary" : ""}
    `}
  >
    {children}
  </button>
);

const ActionButtons = ({
  isPlaying,
  handleOpenCourseClick,
  handleAddToPlaylist,
  handleShare,
  handleLike,
  handleComments,
  isCourseInMyList,
  isLiked,
  likesCount,
  isLoading,
  myListLoading,
  likeLoading,
  engagement,
  hasAccess,
  isAuthenticated,
}) => {
  const { t } = useTranslation();

  const commentsCount = engagement?.course_comments_count || 0;

  // Primary action: Watch / Open course / Unlock
  const PrimaryButton = () => {
    if (!hasAccess) {
      return (
        <button
          type="button"
          onClick={handleOpenCourseClick}
          className="
            flex items-center justify-center gap-x-2
            px-5 py-3 sm:px-6 sm:py-3.5 lg:px-4 lg:py-3
            rounded-xl lg:rounded-md
            bg-primary hover:bg-darkPrimary text-white font-bold
            text-sm sm:text-base
            min-h-[48px] lg:min-h-0
            shadow-[0_6px_14px_rgba(237,26,77,0.35)] lg:shadow-none
            transition-colors
            min-w-[140px] sm:min-w-[160px] lg:min-w-[120px]
          "
        >
          <LockKey size={20} className="lg:!text-2xl" />
          <span>{t("courses.open_courses")}</span>
        </button>
      );
    }

    return (
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={handleOpenCourseClick}
          className="
            flex items-center justify-center gap-x-2
            px-5 py-3 sm:px-6 sm:py-3.5 lg:px-4 lg:py-3
            rounded-xl lg:rounded-md
            bg-primary hover:bg-darkPrimary text-white font-bold
            text-sm sm:text-base
            min-h-[48px] lg:min-h-0
            shadow-[0_6px_14px_rgba(237,26,77,0.35)] lg:shadow-none
            transition-colors
            min-w-[140px] sm:min-w-[160px] lg:min-w-[120px]
          "
        >
          <Video size={20} className="lg:!text-2xl" />
          <span>{t("courses.watch_course")}</span>
        </button>
        <button
          type="button"
          onClick={handleAddToPlaylist}
          aria-label={
            isCourseInMyList ? t("courses.in_my_list") : t("courses.add_to_list")
          }
          title={
            isCourseInMyList ? t("courses.in_my_list") : t("courses.add_to_list")
          }
          className="
            flex items-center justify-center
            p-3 lg:px-4 lg:py-3
            rounded-xl lg:rounded-md
            bg-white/[0.06] border border-white/[0.08] hover:bg-white/[0.10]
            lg:bg-lightGrey lg:border-0 lg:hover:bg-lightGrey/90
            text-white transition-colors
            min-h-[48px] lg:min-h-0 min-w-[48px]
          "
        >
          {isLoading || myListLoading ? (
            <Spinner isWhite isSmall />
          ) : (
            <BookmarkSimple
              size={22}
              className="lg:!text-[28px]"
              weight={isCourseInMyList ? "fill" : "regular"}
            />
          )}
        </button>
      </div>
    );
  };

  // Secondary actions: Like / Comments / Share
  const SecondaryButtons = () => (
    <div className="flex items-center gap-2 sm:gap-3">
      <SecondaryPill
        onClick={handleLike}
        disabled={isLiked || likeLoading}
        active={isLiked}
        label={isLiked ? t("courses.liked") : t("courses.like")}
      >
        {likeLoading ? (
          <Spinner isWhite isSmall />
        ) : (
          <Heart
            size={20}
            className="lg:!text-[28px]"
            weight={isLiked ? "fill" : "regular"}
          />
        )}
        <span className="text-xs sm:text-sm font-medium tabular-nums">
          {likesCount}
        </span>
      </SecondaryPill>

      <SecondaryPill onClick={handleComments} label={t("courses.comments")}>
        <ChatCircle size={20} className="lg:!text-[28px]" />
        <span className="text-xs sm:text-sm font-medium tabular-nums">
          {commentsCount}
        </span>
      </SecondaryPill>

      <SecondaryPill onClick={handleShare} label={t("courses.share")}>
        <ShareFat size={20} className="lg:!text-[28px]" />
      </SecondaryPill>
    </div>
  );

  // When the trailer is playing we use a compact centered row on ALL sizes
  // (desktop behavior unchanged from the original).
  if (isPlaying) {
    return (
      <Container>
        <div className="flex flex-wrap items-center justify-center mt-4 sm:mt-6 gap-2 sm:gap-3">
          <PrimaryButton />
          <SecondaryButtons />
        </div>
      </Container>
    );
  }

  // Not playing: desktop keeps the absolute bottom-right overlay with a
  // bg-black/90 panel (unchanged); mobile flows below the video.
  return (
    <div className="flex items-end w-full mt-6 sm:mt-8 lg:mt-0 lg:absolute lg:bottom-0 lg:right-0 z-10 lg:h-full lg:pointer-events-none">
      <Container>
        <div className="flex flex-wrap items-center justify-center sm:justify-start gap-3 sm:gap-3 lg:gap-x-6 lg:px-12 lg:py-10 relative rounded-tl-xl lg:w-max rounded-tr-xl lg:pointer-events-auto">
          {/* Desktop-only dark panel behind the buttons */}
          <div className="hidden lg:block absolute inset-0 bg-black/90 rounded-tl-xl w-full h-full rounded-tr-xl z-[1]" />

          <div className="flex flex-wrap items-center justify-center sm:justify-start gap-3 sm:gap-3 lg:gap-x-16 relative z-[10]">
            <div className="w-full sm:w-auto flex justify-center sm:justify-start order-2 sm:order-1 mt-0 sm:mt-0">
              <SecondaryButtons />
            </div>
            <div className="w-full sm:w-auto flex justify-center order-1 sm:order-2">
              <PrimaryButton />
            </div>
          </div>
        </div>
      </Container>
    </div>
  );
};

export default ActionButtons;
